import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import { getIdea, IDEAS } from "@/data/ideas";

export default function IdeaPage() {
  const { slug } = useParams();
  const idea = getIdea(slug);

  if (!idea) {
    return (
      <>
        <Header />
        <main className="bg-sand pb-32 pt-44">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h1
              className="font-serif text-4xl tracking-tight text-ink"
              data-testid="idea-not-found"
            >
              Seite nicht gefunden
            </h1>
            <Link
              to="/business-ideen"
              data-testid="idea-back-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay"
            >
              <ArrowLeft className="h-4 w-4" /> Zu den Business-Ideen
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const others = IDEAS.filter((i) => i.slug !== slug);
  const faqTitle = `Häufige Fragen: ${idea.title}`;

  return (
    <>
      <Seo
        title={idea.title}
        description={idea.description}
        keywords={idea.keywords}
        jsonLd={[
          {
            "@type": "Service",
            name: idea.title,
            description: idea.description,
            inLanguage: "de",
            provider: {
              "@type": "ProfessionalService",
              name: "Florida Business & Relocation",
            },
            areaServed: { "@type": "State", name: "Florida" },
          },
          {
            "@type": "FAQPage",
            mainEntity: idea.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://auswandernflorida.com/" },
              { "@type": "ListItem", position: 2, name: "Business-Ideen", item: "https://auswandernflorida.com/business-ideen" },
              { "@type": "ListItem", position: 3, name: idea.title },
            ],
          },
        ]}
      />
      <Header />
      <main>
        <article data-testid="idea-page" className="bg-sand pb-24 pt-36 lg:pt-44">
          <div className="mx-auto max-w-3xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/business-ideen"
                data-testid="idea-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-clay"
              >
                <ArrowLeft className="h-4 w-4" /> Business-Ideen
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-10 text-xs uppercase tracking-[0.3em] text-clay"
              data-testid="idea-badge"
            >
              {idea.badge}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="idea-title"
            >
              {idea.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 border-l-2 border-clay pl-6 font-serif text-xl italic leading-relaxed text-ink-muted"
              data-testid="idea-lead"
            >
              {idea.lead}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10"
            >
              <a
                href="/kontakt"
                data-testid="idea-cta-top"
                className="group inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Diese Idee anfragen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>

            <div className="mt-16 space-y-16" data-testid="idea-body">
              {idea.blocks.map((block, i) => (
                <motion.section
                  key={block.heading}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6 }}
                  data-testid={`idea-section-${i}`}
                >
                  <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                    {block.heading}
                  </h2>
                  {block.paragraphs?.map((p, j) => (
                    <p
                      key={j}
                      className="mt-5 text-base leading-relaxed text-ink-muted"
                    >
                      {p}
                    </p>
                  ))}
                  {block.list && (
                    <ul className="mt-6 space-y-3">
                      {block.list.map((item, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-3 text-base leading-relaxed text-ink-muted"
                        >
                          <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.section>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-16 border-l-2 border-clay bg-sand-deep p-8 sm:p-10"
              data-testid="idea-note-box"
            >
              <p className="flex items-center gap-3 font-serif text-xl italic tracking-tight text-ink sm:text-2xl">
                <Info className="h-5 w-5 shrink-0 text-clay" />
                {idea.myth.title}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {idea.myth.text}
              </p>
            </motion.div>

            <motion.section
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="mt-20"
              data-testid="idea-faq"
            >
              <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                {faqTitle}
              </h2>
              <div className="mt-8 space-y-10">
                {idea.faq.map((f, i) => (
                  <div key={i} data-testid={`idea-faq-item-${i}`}>
                    <h3 className="font-serif text-lg tracking-tight text-ink sm:text-xl">
                      {f.q}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted md:text-base">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>

            <div
              className="mt-16 border border-line bg-sand-deep p-10"
              data-testid="idea-cta-box"
            >
              <p className="font-serif text-2xl italic leading-snug tracking-tight text-ink">
                Wie wir Sie begleiten
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                {idea.closing}
              </p>
              <a
                href="/kontakt"
                data-testid="idea-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenloses Erstgespräch buchen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            <div className="mt-24 border-t border-line pt-12">
              <p className="text-xs uppercase tracking-[0.3em] text-clay">
                Weitere Business-Ideen
              </p>
              <ul className="mt-8 space-y-6" data-testid="idea-more-list">
                {others.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/business-ideen/${other.slug}`}
                      data-testid={`idea-more-${other.slug}`}
                      className="group flex items-baseline justify-between gap-6"
                    >
                      <span className="font-serif text-xl tracking-tight text-ink transition-colors group-hover:text-clay sm:text-2xl">
                        {other.title}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-clay transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
